import Bugsnag from '@bugsnag/js';
import type { BreadcrumbType, Event as BugsnagEvent, NotifiableError, OnErrorCallback } from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import React from 'react';
import type { ErrorInfo } from 'react-dom/client';

import { entitlementsSelectors } from 'app-components/flux/entitlements';
import { subscriptionsSelectors } from 'app-components/flux/subscriptions';
import { transactionsSelectors } from 'app-components/flux/transactions';
import {
  getLogger,
  isDemoMode,
  localPreferences,
  platform,
  requireBuildConfig,
  requireClientConfig,
  tracker,
} from 'app-components/utils/core';
import { applyAxiosErrorGrouping } from 'app-components/utils/core/axiosErrorGrouping';
import { normalizePath, scrubNonErrorTabs, scrubUrl } from 'app-components/utils/core/telemetrySafe';
import store from 'app-components/utils/redux-store';

import { navigate } from 'NavigateSetter';
import { chunkLoadFailureMessage } from 'utils/chunkLoadError';

import { scrubBreadcrumb } from './scrubBreadcrumb';

const logger = getLogger('utils/core/bugsnag');

// Chrome-only and missing from lib.dom.
type PerformanceWithMemory = Performance & { memory?: { jsHeapSizeLimit?: number; usedJSHeapSize?: number } };

// Benign by spec: the browser delivers the skipped notifications next frame. Raised by extension observers and by
// libraries that re-render inside their callback (e.g. the PDF viewer); nothing actionable.
const resizeObserverLoopError = /^ResizeObserver loop (completed with undelivered notifications|limit exceeded)/;

const isResizeObserverLoopError = (event: BugsnagEvent): boolean => {
  const message = typeof event.originalError === 'string' ? event.originalError : event.errors?.[0]?.errorMessage;
  return typeof message === 'string' && resizeObserverLoopError.test(message);
};

declare global {
  interface Window {
    bugsnag: typeof Bugsnag;
  }
}

export const errorsBlackList = [
  // send error to bugsnag, but suppress any other handling - errorClass:errorMessage
  /NetworkError:.*/,
  /Error:Network Error/,
  /HTTPError:.*/,
  /AxiosError:.*/,
  /LaunchDarklyFlagFetchError:.*/,
  /Assert:.*/,
  /Error:valid auth tokens not found locally/,
];

export const treatAsWarning = [
  // send error to bugsnag and popup in dev build, but don't show error page + don't track crash in Mixoanel - errorClass:errorMessage
  'ReferenceError:grecaptcha is not defined',
  'TypeError:can\'t redefine non-configurable property "userAgent"', // some firefox extension specific error
  'TypeError:Cannot redefine property: userAgent', // some chrome extension specific error
  /InvalidError:.*/, // another flavor of unhandled rejection
  /UnhandledRejection:.*/,
  /TypeError:can't access dead object/, // firefox addons error - https://udn.realityripple.com/docs/Web/JavaScript/Reference/Errors/Dead_object
  "NotFoundError:Failed to execute 'removeChild' on 'Node': The node to be removed is not a child of this node.",
  /SecurityError:Failed to read a named property 'print'.*/,
];

// A backstop: these match key names only, never text inside a string, so code must not send these values to begin with.
const redactedKeys = [
  'access_token',
  'accessToken',
  'authorization',
  'client_secret',
  'clientSecret',
  'credentials',
  'dateOfBirth',
  'firstName',
  'institutionLogin',
  'lastName',
  'mfaResponses',
  'middleName',
  'password',
  'primaryAddress',
  'primaryEmail',
  'primaryPhone',
  'refresh_token',
  'refreshToken',
  'socialSecurityNumber',
  'ssn',
  'ssnEncrypted',
  'username',
];

const disableBugsnag = localPreferences.getDisableBugsnag();

let errorAttempts = 0;

let gUserId: string | undefined = undefined;
let gDatasetId: string | undefined = undefined;
export const bugsnagIdentify = (userId?: string | null, datasetId?: string | null): void => {
  gUserId = userId ?? undefined;
  gDatasetId = datasetId ?? undefined;
};

let showErrorPageTimer: NodeJS.Timeout;

export const dismissErrorPage = () => clearTimeout(showErrorPageTimer);

export const bugsnagNotify = (
  error: NotifiableError,
  onError?: OnErrorCallback,
  postReportCallback?: (err: unknown, event: BugsnagEvent) => void,
) => {
  if (!disableBugsnag) {
    Bugsnag.notify(error, onError, postReportCallback);
    dismissErrorPage();
  }
};

// Reports a handled error and, unlike bugsnagNotify, keeps an earlier crash's pending error page.
export const bugsnagNotifyKeepingErrorPage = (error: unknown, onError?: OnErrorCallback) => {
  if (!disableBugsnag) {
    // Bugsnag reports any value (non-errors as InvalidError), but its typings only accept error shapes.
    Bugsnag.notify(error as NotifiableError, onError);
  }
};

// createRoot onRecoverableError. React already recovered, so report a handled warning (no error page) and,
// unlike bugsnagNotify, keep an earlier crash's pending error page.
export const bugsnagNotifyRecoverable = (error: unknown, errorInfo?: ErrorInfo) => {
  // React calls this inside its commit without a catch, so it must not throw.
  try {
    if (disableBugsnag) {
      // biome-ignore lint/suspicious/noConsole: the error reporter can't report its own failures
      console.error(error);
      return;
    }
    // Bugsnag records error.cause as the next exception and normalizes non-Error values itself.
    bugsnagNotifyKeepingErrorPage(error, (event) => {
      event.addMetadata('react', { recoverable: true, componentStack: errorInfo?.componentStack });
    });
  } catch (e) {
    // biome-ignore lint/suspicious/noConsole: the error reporter can't report its own failures
    console.error(e);
  }
};

// A failed chunk the page reloads to fetch again. Info severity sets these apart from the failures that leave the
// error page up. onReported runs once Bugsnag is done with the report, sent or dropped, and never while Bugsnag is
// disabled.
export const bugsnagNotifyChunkLoadReload = (error: Error, onReported: () => void): void =>
  bugsnagNotify(
    error,
    (event) => {
      event.severity = 'info';
      event.addMetadata('chunkLoad', { reload: true });
    },
    onReported,
  );

// ACME_PROVISIONAL and ACME_PREMIER are tiers too but have no name yet.
const datasetTierNames: { [tier: string]: string | undefined } = {
  ACME_LITE: 'Simplifi Lite',
  ACME_BASIC: 'Simplifi',
  ACME_HAB: 'Simplifi Business And Personal',
  UNKNOWN: 'No Tier',
};

if (!disableBugsnag) {
  Bugsnag.start({
    apiKey: requireBuildConfig().bugsnag_api_key,
    appType: `${datasetTierNames['UNKNOWN']}${isDemoMode() ? ' Demo' : ''}`,
    appVersion: requireClientConfig().app_version,
    autoDetectErrors: true,
    autoTrackSessions: true,
    collectUserIp: false,
    enabledBreadcrumbTypes: [
      'error',
      'navigation',
      'request',
      'user',
      // 'log', // sorry no logs until sanitize them
    ],
    logger,
    maxBreadcrumbs: 40,
    maxEvents: 3,
    redactedKeys,
    releaseStage: requireClientConfig().environment,
    reportUnhandledPromiseRejectionsAsHandled: true,
    metadata: {
      device: {
        device: platform?.device.type,
        memoryDevice:
          'deviceMemory' in navigator && typeof navigator.deviceMemory === 'number'
            ? navigator.deviceMemory * 1024 ** 3
            : undefined,
        hostname: window?.location?.hostname,
      },
    },
    plugins: [new BugsnagPluginReact(React)],
    enabledErrorTypes: {
      unhandledExceptions: true,
      unhandledRejections: false, // we introduce lint rule to catch unhandled rejections (promise/catch-or-return)
    },
    onBreadcrumb: scrubBreadcrumb,
    onError: (event) => {
      if (isResizeObserverLoopError(event)) {
        return false;
      }

      // The URL can hold tokens and search text, and the path ids and tickers. Bugsnag fills both in from the page.
      if (event.request?.url) {
        event.request.url = scrubUrl(event.request.url);
      }
      if (event.context === window.location.pathname) {
        event.context = normalizePath(window.location.pathname);
      }
      scrubNonErrorTabs(event);

      const [firstError] = event.errors;
      // Report a failed import() as webpack's ChunkLoadError was. The browser's own TypeError only: an Assert that
      // quotes one keeps its class, and with it its blacklist entry.
      if (firstError?.errorClass === 'TypeError' && chunkLoadFailureMessage.test(firstError.errorMessage)) {
        firstError.errorClass = 'ChunkLoadError';
        const url = firstError.errorMessage.match(/https?:\/\/\S+/)?.[0];
        if (url) {
          event.addMetadata('chunkLoad', { url });
        }
      }

      const sendReport = !localPreferences.getIgnoreBugsnagReport();
      if (event.originalError) {
        // biome-ignore lint/suspicious/noConsole: keeps the original error in the dev console
        console.error(event.originalError);
      }

      const newEvent = event;

      // Signed out there's no id to set, and clearing it would also clear the anonymous device id Bugsnag put there.
      if (gUserId) {
        newEvent.setUser(gUserId);
      }

      const datasetTier = store ? entitlementsSelectors.getTopTierEntitlement(store.getState())?.id : undefined;
      const userTier = store ? subscriptionsSelectors.getActiveSubscription(store.getState())?.tierUriName : undefined;
      newEvent.addMetadata('user', { datasetId: gDatasetId, datasetTier, userTier });

      newEvent.app.type = `${datasetTierNames[datasetTier || 'UNKNOWN']}${isDemoMode() ? ' Demo' : ''}`;

      const memoryLimit = (window?.performance as PerformanceWithMemory | undefined)?.memory?.jsHeapSizeLimit;
      const memoryUsed = (window?.performance as PerformanceWithMemory | undefined)?.memory?.usedJSHeapSize;
      const memoryAvailable =
        typeof memoryLimit === 'number' && typeof memoryUsed === 'number' ? memoryLimit - memoryUsed : undefined;
      const transactions = store ? transactionsSelectors.getResourcesById(store.getState())?.size : null;
      const online = navigator.onLine;
      newEvent.addMetadata('device', { memoryUsed, memoryAvailable, memoryLimit, transactions, online });

      applyAxiosErrorGrouping(newEvent, {
        extraMetadata: { page: newEvent.context },
        networkStatus: navigator.onLine === false ? 'offline' : 'online',
      });

      const error = event?.errors?.[0];
      const { errorClass, errorMessage, stacktrace, type } = error;

      const errorSource = stacktrace?.[0] || event.originalError;
      // A stack frame only: the original error can be any object, such as a failed request with its headers. The SDK
      // strips a frame's query string after this runs, and an inline script's file is the page URL.
      const errorDetails = stacktrace?.[0]
        ? JSON.stringify({ ...stacktrace[0], file: stacktrace[0].file?.split(/[?#]/)[0] })
        : errorMessage;
      const props = {
        href: scrubUrl(window.location.href),
        path: normalizePath(window.location.pathname),
        exception_type: type,
        exception_class: errorClass,
        exception_name: errorMessage,
        exception_reason: errorDetails,
      };
      tracker.track(tracker.events.exception, props);

      const context = `${errorClass}:${errorMessage}`;
      if (!sendReport) {
        logger.warn('send crash report is suppressed from settings - report not gonna be sent...');
      }
      if (errorsBlackList.find((mask) => (typeof mask === 'string' ? context.includes(mask) : mask.test?.(context)))) {
        logger.warn(`error blacklisted: '${context}`, newEvent);
        newEvent.severity = 'warning';
        newEvent.unhandled = false;
      } else {
        logger.warn(`error will be sent to bugsnag: '${context}`, newEvent);
        let showErrorPage =
          !treatAsWarning.find((mask) => (typeof mask === 'string' ? context.includes(mask) : mask.test?.(context))) &&
          newEvent.unhandled &&
          (!errorSource?.file || errorSource?.file?.startsWith(window.location.origin));
        if (!showErrorPage) {
          logger.warn(`error ignored: '${context}`);
          newEvent.severity = 'warning';
          newEvent.unhandled = false;
        }

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
        const suppressWindowError = localPreferences.getSuppressWindowError();
        if (!suppressWindowError) {
          alert(`Send Error Report to Bugsnag:\
          \nUse 'dev menu > Crash > Ignore Bugsnag Reports' to disable popups\
          \n\n${event.originalError || error}`);
          showErrorPage = false;
        }
/////////////////

        if (showErrorPage) {
          clearTimeout(showErrorPageTimer);
          showErrorPageTimer = setTimeout(() => {
            tracker.track(tracker.events.crash, props);
            setTimeout(() => {
              if (window.location.pathname !== '/error' && !errorAttempts) {
                errorAttempts += 1;
                navigate('/error');
              }
            }, 1000);
          }, 1000);
        }
      }

      return sendReport;
    },
  });
}

typeof window !== 'undefined' && (window.bugsnag = Bugsnag);

export const bugsnagBreadcrumb = (name: string, metadata?: { [key: string]: unknown }, type?: BreadcrumbType) => {
  if (!disableBugsnag) {
    Bugsnag.leaveBreadcrumb(name, metadata, type);
  }
};

export const BugsnagErrorBoundary = !disableBugsnag
  ? Bugsnag.getPlugin('react')?.createErrorBoundary(React)
  : undefined;

export default !disableBugsnag && Bugsnag;
